import { inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatOptionSelectionChange } from '@angular/material/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { debounceTime, filter, Observable, Subscription, tap } from 'rxjs';
import { FormType } from 'src/app/shared/interfaces/form-type';
import { AddressChoice } from 'src/app/api/v1/models';
import { CitiesService } from 'src/app/services/cities.service';
import { FormStepAbstract } from '../../../../../shared/components/abstract/form-step.abstract';
import { SessionRepository } from '../../../../../shared/repositories/session.repository';

interface IIndexAddressControl {
  postCode: string | null;
  street: string | null;
  house: string | null;
  flat: string | null;
  city: string | null;
}

@UntilDestroy()
export class IndexAddressStepAbstract extends FormStepAbstract implements OnInit {
  private cities: CitiesService = inject(CitiesService);
  private sessionRepository: SessionRepository = inject(SessionRepository);
  private isInitPostcode: boolean = true;

  override form: FormGroup<FormType<IIndexAddressControl>> = new FormGroup<FormType<IIndexAddressControl>>({
    postCode: new FormControl<string | null>(null, [Validators.required]),
    street: new FormControl<string | null>(null, [Validators.required]),
    house: new FormControl<string | null>(null),
    flat: new FormControl<string | null>(null),
    city: new FormControl<string | null>(null),
  });

  streetControl: FormControl<string> = new FormControl<string>('', {nonNullable: true});

  isLoadingCities$: Observable<boolean> = this.cities.isLoading$;

  postcodeAddress$: Observable<AddressChoice[] | null> = this.cities.postcodeAddress$;

  override ngOnInit(): void {
    super.ngOnInit();

    this.subscribeToDeliveryAddress();

    this.form.controls.postCode.valueChanges.pipe(
      debounceTime(650),
      untilDestroyed(this),
      filter((postcode: string | null): postcode is string => !!postcode)
    ).subscribe((postcode: string) => {
      this.onChangePostcode(postcode);
    });

    this.form.controls.flat.valueChanges.pipe(
      debounceTime(650),
      untilDestroyed(this),
    ).subscribe(() => {
      this.sessionRepository.setDeliveryAddress(this.form.getRawValue());
    })
  }

  private addressSub: Subscription;
  private subscribeToDeliveryAddress(){
    this.addressSub?.unsubscribe();
    this.addressSub = this.sessionRepository.deliveryAddress$.pipe(
      tap(deliveryAddress => {
        if(!deliveryAddress){return}

        this.form.controls.postCode.setValue(deliveryAddress.postCode || '', {emitEvent: this.isInitPostcode});
        this.form.controls.city.setValue(deliveryAddress.city || '', { emitEvent: false });
        this.form.controls.street.setValue(deliveryAddress.street || '', { emitEvent: false });
        this.form.controls.flat.setValue(deliveryAddress.flat || '', { emitEvent: false });
        this.form.controls.house.setValue(deliveryAddress.house || '', { emitEvent: false });

        this.streetControl.setValue(`${deliveryAddress.house}, ${deliveryAddress.street}, ${deliveryAddress.city}`);

        this.isInitPostcode = false;
      }),
      untilDestroyed(this)
    ).subscribe();
  }

  onChangePostcode(postcode: string): void {
    if (postcode.length < 5 || postcode.length > 10) {
      return;
    }
    this.cities.loadAddressByPostcode(postcode);
  }

  onChangeAddress(address: AddressChoice, $event: MatOptionSelectionChange): void {
    if (!$event.isUserInput) { return; }

    this.form.controls.street.setValue(address.street);
    this.form.controls.house.setValue(address.building);
    this.form.controls.city.setValue(address.city);

    this.sessionRepository.setDeliveryAddress(this.form.getRawValue());

    this.cities.getDeliveryZoneByCoordinates({
      lat: address.latitude,
      lng: address.longitude,
    }).pipe(
      untilDestroyed(this)
    ).subscribe();
  }
}
